'use client';

import classNames from 'classnames';
import { Button } from '@/components/ui';
import { BUTTON_SIZES } from '@/components/ui/Buttons/utils';
import { unpauseSubEngineSubscriptions } from '@/lib/api/subscriptionManager/apis/subEngine/unpauseSubEngineSubscriptions';
import { FC, memo, useState } from 'react';
import { useRouter } from '@/navigation';
import { TypeResponseError, TypeResponseSuccess } from '@/lib/api/config';
import { TypePauseReleaseResponseData } from '@/lib/api/subscriptionManager/types';

export type UnpauseERCButtonProps = {
  t: Record<string, string>;
  fullWidth?: boolean;
  small?: boolean;
  ercSubscriptionId: string;
  onUnpause?: () => void;
};

const UnpauseERCButton: FC<UnpauseERCButtonProps> = ({
  t,
  fullWidth,
  small,
  ercSubscriptionId,
  onUnpause,
}) => {
  const { push } = useRouter();
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleUnpauseAction = async (event?: React.MouseEvent) => {
    event?.stopPropagation();

    setIsLoading(true);
    setError(false);

    try {
      const res = await unpauseSubEngineSubscriptions({
        subscriptionUuid: ercSubscriptionId,
      });

      if (!res.ok) {
        const errorResponse = res as TypeResponseError;

        console.error(
          '[unpauseErcSubscription] Unpause failed:',
          errorResponse.errorMessage,
        );

        setError(true);

        return;
      }

      const successfulResponse =
        res as TypeResponseSuccess<TypePauseReleaseResponseData>;

      if (onUnpause) {
        onUnpause();
      } else {
        push(
          `/account/early-reader-club?selected_subscription_uuid=${successfulResponse.data.pause_release.subscription_id}`,
        );
      }
    } catch (error) {
      console.error('[unpauseSubEngineSubscriptions] Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        testId="unpause-btn"
        ctaText={t.unpause}
        onClick={handleUnpauseAction}
        fullWidth={fullWidth}
        disabled={isLoading}
        includeSpinner={isLoading}
        size={small ? BUTTON_SIZES.sm : BUTTON_SIZES.md}
        classes={classNames({ 'h-8 !px-4 py-0 text-sm !border-none': small })}
      />
      {error && <p className="mt-2 text-warning">{t.errorText}</p>}
    </>
  );
};

export default memo(UnpauseERCButton);
